/**
 * Stripe Price Service
 *
 * Calls Stripe price endpoints via gobodo-api-promise (StripeAccountsApi).
 * Maps between app StripePriceRecord and API PriceInput / StripePrice / PriceResponse.
 * Used only when shouldUseGobodo() is true.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../gobodo/gobodoClient';
import type { StripePriceRecord, PriceTier } from '../types/index';

function normalizeTiers(tiersRaw: any): PriceTier[] | undefined {
  if (!Array.isArray(tiersRaw) || tiersRaw.length === 0) return undefined;
  return tiersRaw.map((t: any) => {
    const upToRaw = t.up_to ?? t.upTo;
    const upTo =
      upToRaw === 'inf' || upToRaw === undefined
        ? ('inf' as const)
        : typeof upToRaw === 'number'
          ? upToRaw
          : parseInt(String(upToRaw), 10);
    const unitAmount = t.unit_amount ?? t.unitAmount;
    const flatAmount = t.flat_amount ?? t.flatAmount;
    return {
      upTo: typeof upTo === 'number' && !isNaN(upTo) ? upTo : 'inf',
      ...(unitAmount != null && { unitAmount: Number(unitAmount) }),
      ...(flatAmount != null && { flatAmount: Number(flatAmount) }),
    };
  });
}

/** Normalize API response (StripePrice or PriceResponse with .price) to flat shape; each price has productId string. */
function normalizeStripePrice(raw: any): {
  id: string;
  productId: string;
  currency: string;
  unitAmount: number;
  active: boolean;
  metadata: Record<string, string> | undefined;
  created: number;
  recurring?: { interval: string; intervalCount?: number };
  billingScheme?: 'per_unit' | 'tiered';
  tiersMode?: 'graduated' | 'volume';
  tiers?: PriceTier[];
} {
  const price = raw?.price ?? raw?.stripePrice ?? raw;
  const id = price?.id ?? raw?.id ?? '';
  const productRaw =
    price?.product ??
    price?.productId ??
    raw?.product ??
    raw?.productId ??
    raw?.product_id ??
    price?.product_id;
  const productId =
    typeof productRaw === 'object' && productRaw != null && typeof productRaw.id === 'string'
      ? productRaw.id
      : String(productRaw ?? '');
  const currency = (price?.currency ?? raw?.currency ?? '').toString().toUpperCase();
  const unitAmount = price?.unitAmount ?? raw?.unitAmount ?? 0;
  const active = price?.active ?? raw?.active ?? true;
  const metadata = price?.metadata ?? raw?.metadata;
  const created = price?.created ?? raw?.created ?? 0;
  const recurring = price?.recurring ?? raw?.recurring;
  const billingSchemeRaw = price?.billingScheme ?? raw?.billingScheme ?? price?.billing_scheme ?? raw?.billing_scheme;
  const billingScheme =
    billingSchemeRaw === 'tiered' ? 'tiered' : billingSchemeRaw === 'per_unit' ? 'per_unit' : undefined;
  const tiersModeRaw = price?.tiersMode ?? raw?.tiersMode ?? price?.tiers_mode ?? raw?.tiers_mode;
  const tiersMode = tiersModeRaw === 'volume' ? 'volume' : tiersModeRaw === 'graduated' ? 'graduated' : undefined;
  const tiersRaw = price?.tiers ?? raw?.tiers;
  const tiers = normalizeTiers(tiersRaw);
  return {
    id,
    productId,
    currency,
    unitAmount,
    active,
    metadata: metadata && typeof metadata === 'object' ? metadata : undefined,
    created,
    recurring: recurring
      ? {
          interval: recurring.interval ?? 'month',
          intervalCount: recurring.intervalCount,
        }
      : undefined,
    ...(billingScheme && { billingScheme }),
    ...(tiersMode && { tiersMode }),
    ...(tiers && tiers.length > 0 && { tiers }),
  };
}

function stripePriceToRecord(raw: any): StripePriceRecord {
  const n = normalizeStripePrice(raw);
  const interval = n.recurring?.interval as StripePriceRecord['recurring'] extends { interval: infer I } ? I : never;
  return {
    id: n.id,
    productId: n.productId,
    currency: n.currency,
    unitAmount: n.unitAmount,
    active: n.active,
    metadata: n.metadata,
    created: n.created,
    ...(n.recurring && {
      recurring: {
        interval: interval === 'day' || interval === 'week' || interval === 'month' || interval === 'year' ? interval : 'month',
        intervalCount: n.recurring.intervalCount,
      },
    }),
    ...(n.billingScheme && { billingScheme: n.billingScheme }),
    ...(n.tiersMode && { tiersMode: n.tiersMode }),
    ...(n.tiers && n.tiers.length > 0 && { tiers: n.tiers }),
  };
}

export type CreatePricePayload = {
  productId: string;
  currency: string;
  unitAmount: number;
  active?: boolean;
  metadata?: Record<string, string>;
  recurring?: {
    interval: 'day' | 'week' | 'month' | 'year';
    intervalCount?: number;
  };
  /** Associate price with a pricebook (backend links price to pricebook). */
  pricebookId?: number;
  pricebookKey?: string;
  /** When 'tiered', use tiers instead of unitAmount. */
  billingScheme?: 'per_unit' | 'tiered';
  tiersMode?: 'graduated' | 'volume';
  tiers?: PriceTier[];
};

export type UpdatePricePayload = {
  active?: boolean;
  metadata?: Record<string, string>;
};

export async function listPrices(): Promise<StripePriceRecord[]> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client);
  const data = await api.listPrices();
  const list = Array.isArray(data) ? data : [];
  return list.map((item: any) => stripePriceToRecord(item));
}

export async function listPricesByProduct(productId: string): Promise<StripePriceRecord[]> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client);
  const data = await api.listStripePricesByProduct(productId);
  const list = Array.isArray(data) ? data : [];
  return list.map((item: any) => stripePriceToRecord(item));
}

export async function getPrice(priceId: string): Promise<StripePriceRecord> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client);
  const data = await api.getStripePrice(priceId);
  return stripePriceToRecord(data);
}

export async function createPrice(payload: CreatePricePayload): Promise<StripePriceRecord> {
  const client = getGobodoClient();
  const Api = GobodoApi as any;
  const api = new Api.StripeAccountsApi(client);
  const isTiered =
    payload.billingScheme === 'tiered' && payload.tiers != null && payload.tiers.length > 0;
  const input = new Api.PriceInput(payload.productId, payload.currency);
  if (isTiered) {
    input.setBillingScheme(Api.PriceInput.BillingSchemeEnum?.tiered ?? 'tiered');
    input.setTiersMode(
      payload.tiersMode === 'volume'
        ? (Api.PriceInput.TiersModeEnum?.volume ?? 'volume')
        : (Api.PriceInput.TiersModeEnum?.graduated ?? 'graduated')
    );
    const tierInputs = payload.tiers!.map((t) => {
      const upTo = t.upTo === 'inf' ? 'inf' : t.upTo;
      const unitAmt = t.unitAmount ?? 0;
      const tier = new Api.TierInput(upTo, unitAmt);
      if (t.flatAmount != null) {
        tier.setFlatAmount(t.flatAmount);
      }
      return tier;
    });
    input.setTiers(tierInputs);
  } else {
    input.setUnitAmount(payload.unitAmount);
  }
  input.setActive(payload.active !== false);
  if (payload.metadata && Object.keys(payload.metadata).length > 0) {
    input.setMetadata(payload.metadata);
  }
  if (payload.recurring) {
    const recurring = new Api.RecurringInput(payload.recurring.interval);
    if (payload.recurring.intervalCount != null) {
      recurring.setIntervalCount(payload.recurring.intervalCount);
    }
    input.setRecurring(recurring);
  }
  if (payload.pricebookId != null) {
    input.setPricebookId(payload.pricebookId);
  }
  if (payload.pricebookKey != null && payload.pricebookKey !== '') {
    input.setPricebookKey(payload.pricebookKey);
  }
  const data = await api.createStripePrice(input);
  return stripePriceToRecord(data);
}

export async function updatePrice(priceId: string, updates: UpdatePricePayload): Promise<StripePriceRecord> {
  const client = getGobodoClient();
  const Api = GobodoApi as any;
  const api = new Api.StripeAccountsApi(client);
  const existing = await getPrice(priceId);
  const input = new Api.PriceInput(existing.productId, existing.currency);
  if (existing.billingScheme === 'tiered' && existing.tiers && existing.tiers.length > 0) {
    input.setBillingScheme(Api.PriceInput.BillingSchemeEnum?.tiered ?? 'tiered');
    input.setTiersMode(
      existing.tiersMode === 'volume'
        ? (Api.PriceInput.TiersModeEnum?.volume ?? 'volume')
        : (Api.PriceInput.TiersModeEnum?.graduated ?? 'graduated')
    );
    const tierInputs = existing.tiers.map((t) => {
      const upTo = t.upTo === 'inf' ? 'inf' : t.upTo;
      const unitAmt = t.unitAmount ?? 0;
      const tier = new Api.TierInput(upTo, unitAmt);
      if (t.flatAmount != null) {
        tier.setFlatAmount(t.flatAmount);
      }
      return tier;
    });
    input.setTiers(tierInputs);
  } else {
    input.setUnitAmount(existing.unitAmount);
  }
  input.setActive(updates.active !== undefined ? updates.active : existing.active);
  input.setMetadata(updates.metadata !== undefined ? updates.metadata : existing.metadata ?? {});
  const data = await api.updatePrice(priceId, input);
  return stripePriceToRecord(data);
}

export async function deletePrice(priceId: string): Promise<void> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client);
  await api.deleteStripePrice(priceId);
}
